import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import type { TFunction } from "i18next";
import { XIcon } from "lucide-react";

import { useConfiguration } from "@/api/configuration";
import { useDismissInsight, type Insight } from "@/api/insights";
import { useLocationName } from "@/api/locations";
import { useSegmentName } from "@/api/segments";
import { Button } from "@/components/ui/button";
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemTitle,
} from "@/components/ui/item";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  formatCurrency,
  formatDate,
  formatNumber,
  formatPercent,
} from "@/utils/formatters";

function headline(
  insight: Insight,
  t: TFunction,
  segmentName: (id: number) => string,
): string {
  const { facts } = insight;
  switch (insight.detector) {
    case "coverage_gap":
      return t("insights.coverageGap", {
        quantity: formatNumber(facts.quantity as number),
        months: facts.months,
      });
    case "stale_demand":
      return t("insights.staleDemand", {
        date: formatDate(facts.latest as string),
      });
    case "stale_stock_count":
      return t("insights.staleStockCount", {
        date: formatDate(facts.latest as string),
      });
    case "demand_shift": {
      const actual = facts.actual as number;
      const expected = facts.expected as number;
      const shift = t(
        actual < expected ? "insights.demandDrop" : "insights.demandRise",
        {
          actual: formatNumber(actual),
          expected: formatNumber(expected),
          months: facts.months,
          percent: formatPercent(Math.abs(actual / expected - 1)),
        },
      );
      const driver = facts.driver
        ? t("insights.demandShiftDriver", {
            segment: segmentName(facts.driver as number),
          })
        : "";
      return [shift, driver, actual < expected ? stockNote(facts, t) : ""]
        .filter(Boolean)
        .join(" ");
    }
    case "stopped_selling":
      return [
        t("insights.stoppedSelling", {
          count: facts.silentMonths as number,
          selling: facts.sellingMonths,
          months: facts.months,
        }),
        stockNote(facts, t),
      ]
        .filter(Boolean)
        .join(" ");
  }
}

function stockNote(facts: Insight["facts"], t: TFunction): string {
  const shortDays = facts.shortDays as number | null;
  return shortDays
    ? t("insights.lowStock", { short: shortDays, days: facts.days })
    : "";
}

export function InsightItem({ insight }: { insight: Insight }) {
  const { t } = useTranslation();
  const locationName = useLocationName();
  const segmentName = useSegmentName();
  const { data: config } = useConfiguration();
  const currency = config?.currencyCode ?? "EUR";
  const dismiss = useDismissInsight();

  return (
    <Item variant="outline">
      <ItemContent>
        <ItemTitle>{headline(insight, t, segmentName)}</ItemTitle>
        <ItemDescription>
          {insight.item ? (
            <Link to={`/items/${insight.item}?loc=${insight.location}`}>
              {[
                insight.itemName,
                insight.itemSku,
                locationName(insight.location),
              ]
                .filter(Boolean)
                .join(" · ")}
            </Link>
          ) : (
            <Link to="/settings/integrations">
              {t("settings.rail.integrations")}
            </Link>
          )}
        </ItemDescription>
      </ItemContent>
      <ItemActions>
        {insight.impact > 0 && (
          <span className="text-muted-foreground tabular-nums">
            {formatCurrency(insight.impact, currency, {
              maximumFractionDigits: 0,
            })}
          </span>
        )}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              aria-label={t("insights.dismiss")}
              disabled={dismiss.isPending}
              onClick={() => dismiss.mutate(insight.id)}
            >
              <XIcon />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t("insights.dismiss")}</TooltipContent>
        </Tooltip>
      </ItemActions>
    </Item>
  );
}
